import * as React from "react";
import { isObject, object, compact, map, contains, pluck } from "underscore";
import * as moment from "moment";
import { connect } from "react-redux";
import { AppShape, DocFieldShape, DocSectionShape } from "@eave/shared/types/api";

import { getDocById, getSortedDocsOfType } from "underwriter_dashboard/shared";
import DocEntryView from "./DocEntryView";
import { addressObjToStr, addressStrToObj, removeKeysWithEmptyValues } from "./Documents/helpers";
import { MARK_FOR_DELETION } from "./Documents/values";

require("../../../assets/stylesheets/components/underwriter_dashboard/shared/doc_entry.scss");

// Known bug issue in immutability-helper package
// Fix to be released soon:
// https://github.com/kolodny/immutability-helper/pull/98
//
// import update from "immutability-helper";
const update: any = require("immutability-helper");

/* shared */
const TRUE_VALUE = "true";
const FALSE_VALUE = "false";

// since fields can be malformed from their source (ocr, external doc ingestion services),
// we need to flag them and only allow modification into a correctly formatted value.
// we are checking the raw string values, not the typed values in state
export function doesFieldTypeMatchValueFormat(fieldType, fieldValue, fieldValueOptions) {
  // an empty value always matches, we don't call empties malformed
  if (fieldValue === "") {
    return true;
  }
  switch (fieldType) {
    case "boolean": {
      // falsey is false and normally we just delete from the server, but
      // only allow actual persisted values returning to be true/false
      return fieldValue === TRUE_VALUE || fieldValue === FALSE_VALUE;
    }
    case "money": // fall thru
    case "number": {
      return !Number.isNaN(fieldValue);
    }
    case "address": {
      try {
        addressStrToObj(fieldValue);
        return true;
      } catch (e) {
        return false;
      }
    }
    case "date": {
      return moment(fieldValue, "YYYY-MM-DD", true).isValid();
    }
    case "select": {
      if (!fieldValueOptions) {
        throw new Error(`field type ${fieldType} configured as select but no options present`);
      }
      return contains(pluck(fieldValueOptions, "value"), fieldValue);
    }
    case "": // fall thru, default for untyped
    case "text": {
      return true;
    }
    default: {
      throw new Error(`cannot verify string format for field type ${fieldType}`);
    }
  }
}

interface DocEntryProps {
  dispatch?: Function;
  docId?: number;
  app: AppShape;
  docType?: string;
  hurdle: any;
  docNotFoundMsg: string;
  modalTitle?: string;
  hideButton?: boolean;
  onHide: Function;
  onSave?: Function; // Hurdles can OPTIONALLY specify additional save() functions to be called along with the standard document-field saving
  blockLoadAppOnSave?: boolean;
}

class DocEntry extends React.Component<DocEntryProps, any> {
  static initialState(props: DocEntryProps) {
    const { docId, app, docType } = props;

    if (!docId && !docType) {
      throw new Error("did not specify docId or docType");
    } else if (docId && docType) {
      throw new Error("cannot specify both docId and docType");
    }

    let docs;
    if (docId) {
      const d = getDocById(app.documents, docId);
      docs = d ? [d] : []; // wrap in array for consistency
    } else {
      docs = getSortedDocsOfType(app.documents, docType);
    }

    if (!docs || !docs.length) {
      return {
        docs: null,
        mainDoc: null,
        values: null,
      };
    }

    // HACK: jjw: for some doc types (e.g., PC), we can only get complete
    // info by letting the operator look at a range of docs (in this case,
    // might need addenda to figure out the complete PC agreement); so by
    // convention, we store inputs for that whole range in document_fields
    // rows corresponding to the oldest doc's ID (should redo in the future)

    return {
      docs,
      mainDoc: docs[0], // oldest
      values: DocEntry.docStructToEditorValues(docs[0]),
    };
  }

  static strToTypedFieldValue(fieldType, str) {
    switch (fieldType) {
      case "boolean": {
        // while all values inputted from the operator dashboard will be true or empty
        // they may have other values from other sources
        return str === TRUE_VALUE;
      }
      case "money": // fall thru
      case "number": {
        // TODO backend is stripping commas may need to also strip commas here eventually
        //      as parseFloat cannot deal with them
        return parseFloat(str);
      }
      case "address": {
        return addressStrToObj(str);
      }
      case "": // fall thru, default for untyped
      case "date": // fall thru
      case "select": // fall thru
      case "text": {
        return str;
      }
      default: {
        throw new Error(`cannot convert string to field type ${fieldType}`);
      }
    }
  }

  static typedFieldValueToStr(fieldValue, fieldType) {
    switch (fieldType) {
      case "boolean": {
        return fieldValue ? TRUE_VALUE : FALSE_VALUE;
      }
      case "address": {
        return addressObjToStr(fieldValue);
      }
      case "money": // fall thru
      case "number": {
        return `${fieldValue}`;
      }
      case "": // fall thru, default for untyped
      case "date": // fall thru
      case "select": // fall thru
      case "text": {
        return fieldValue;
      }
      default: {
        throw new Error(`cannot convert field type ${fieldType} to string`);
      }
    }
  }

  static docStructToEditorValues(doc) {
    const sectionValuesMap = {};
    const sectionsAndCalculations = [...doc.sections, doc.calculations];

    sectionsAndCalculations.forEach((section: DocSectionShape) => {
      if (!section || !section.section_key) {
        return null;
      }

      const sectionValues = object(
        compact(
          map(section.fields, (field: DocFieldShape) => {
            if (!field.field_value.length) {
              return null;
            }

            // check type and blank out the editor value (but keep the key)
            // if it is not a valid format
            let editorFieldValue = "";
            if (
              doesFieldTypeMatchValueFormat(
                field.field_type,
                field.field_value,
                field.field_value_options,
              )
            ) {
              editorFieldValue = DocEntry.strToTypedFieldValue(field.field_type, field.field_value);
            }

            return [field.field_key, editorFieldValue];
          }),
        ),
      );

      if (!Object.keys(sectionValues).length) {
        return null;
      }

      if (!sectionValuesMap[section.section_key]) {
        sectionValuesMap[section.section_key] = {};
      }
      sectionValuesMap[section.section_key][section.section_index] = sectionValues;
    });

    return sectionValuesMap;
  }

  constructor(props) {
    super(props);
    this.state = DocEntry.initialState(props);
  }

  upsertValue = (sectionKey: string, sectionIndex: number, fieldKey: string, fieldValue: any) => {
    const { values } = this.state;

    let val;
    if (isObject(fieldValue)) {
      // address values are objects
      val = removeKeysWithEmptyValues(fieldValue);
    } else {
      val = fieldValue;
    }

    let updatedValues;
    if (values[sectionKey]) {
      if (values[sectionKey][sectionIndex]) {
        updatedValues = update(values, {
          [sectionKey]: {
            [sectionIndex]: {
              [fieldKey]: {
                $set: val,
              },
            },
          },
        });
      } else {
        updatedValues = update(values, {
          [sectionKey]: {
            [sectionIndex]: {
              $set: {
                [fieldKey]: val,
              },
            },
          },
        });
      }
    } else {
      updatedValues = update(values, {
        [sectionKey]: {
          $set: {
            [sectionIndex]: {
              [fieldKey]: val,
            },
          },
        },
      });
    }

    this.setState({ values: updatedValues });
  };

  // Rather than directly deleting a value, we update instead with an empty string, so that a "deletion" diff object is generated
  deleteValue = (sectionKey: string, sectionIndex: number, fieldKey: string) => {
    this.upsertValue(sectionKey, sectionIndex, fieldKey, MARK_FOR_DELETION);
  };

  getHighestSectionIndex = (sectionKey: string): number => {
    const { docs } = this.state;

    const sectionIndexes: number[] = docs[0].sections
      .filter((section: DocSectionShape) => section.section_key === sectionKey)
      .map((section: DocSectionShape) => section.section_index);

    return Math.max(...sectionIndexes);
  };

  addAnotherSection = (sectionKey: string, indexOfSectionToAddAfter: number) => {
    const { docs } = this.state;

    const highestSectionIndex: number = this.getHighestSectionIndex(sectionKey);
    const newSectionIndex = highestSectionIndex + 1;

    const newDocSection = { ...docs[0].sections[indexOfSectionToAddAfter] };
    newDocSection.section_index = newSectionIndex;
    docs[0].sections.splice(indexOfSectionToAddAfter + 1, 0, newDocSection);

    this.setState({ docs });
  };

  render() {
    const {
      app,
      dispatch,
      docNotFoundMsg,
      modalTitle,
      hideButton,
      hurdle,
      onHide,
      onSave,
    } = this.props;
    const { values, docs } = this.state;

    return (
      <DocEntryView
        dispatch={dispatch}
        app={app}
        hurdle={hurdle}
        onHide={onHide}
        onSave={onSave}
        docs={docs}
        values={values}
        modalTitle={modalTitle}
        hideButton={hideButton}
        upsertValue={this.upsertValue}
        deleteValue={this.deleteValue}
        docNotFoundMsg={docNotFoundMsg}
        addAnotherSection={this.addAnotherSection}
      />
    );
  }
}

// Passing in mapStateToProps was the only thing that quieted TS errors:
const mapStateToProps = () => ({});

const ConnectedDocEntry = connect<any, any, DocEntryProps>(mapStateToProps)(DocEntry);
export default ConnectedDocEntry;
